import {MAVISMART_BACKEND_URL} from '@shared/env';

import {createDataStore} from '@shared_web/lib/data_store';
import {notifyError} from '@shared_web/lib/notification';

// Types

export interface AgentSource {
  type: 'document' | 'api';
  name: string;
  /** Page in the source document. Only ever set on `document` sources. */
  page?: string;
}

export interface AgentMetadata {
  toolsUsed: {name: string; durationMs: number}[];
  tokens: {input: number; output: number; total: number};
  estimatedCostUsd: number;
  modelId: string;
  durationMs: number;
}

export interface AgentMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: AgentSource[];
  metadata?: AgentMetadata;
  isError?: boolean;
  timestamp: number;
}

export type ModelTier = 'fast' | 'medium' | 'slow';

export interface AgentContext {
  pageName: string;
  route: string;
  doorId?: string;
  groupId?: string;
  locale: string;
}

const MODEL_TIER_STORAGE_KEY = 'agent_model_tier';

function getStoredModelTier(): ModelTier {
  try {
    const stored = localStorage.getItem(MODEL_TIER_STORAGE_KEY);
    if (stored === 'fast' || stored === 'medium' || stored === 'slow') return stored;
  } catch {}
  return 'medium';
}

// Stores

const messagesStore = createDataStore<AgentMessage[]>([]);
const modelTierStore = createDataStore<ModelTier>(getStoredModelTier());
const isLoadingStore = createDataStore<boolean>(false);
const panelOpenStore = createDataStore<boolean>(false);

// Hooks

export const useAgentMessages = messagesStore.useData;
export const useAgentModelTier = modelTierStore.useData;
export const useAgentIsLoading = isLoadingStore.useData;
export const useAgentPanelOpen = panelOpenStore.useData;

export function useAgentStore(): {
  messages: AgentMessage[];
  modelTier: ModelTier;
  isLoading: boolean;
  panelOpen: boolean;
} {
  return {
    messages: messagesStore.useData(),
    modelTier: modelTierStore.useData(),
    isLoading: isLoadingStore.useData(),
    panelOpen: panelOpenStore.useData(),
  };
}

// Actions

export function togglePanel(): void {
  panelOpenStore.setData(!panelOpenStore.getData());
}

export function setModelTier(tier: ModelTier): void {
  modelTierStore.setData(tier);
  try {
    localStorage.setItem(MODEL_TIER_STORAGE_KEY, tier);
  } catch {}
}

export function reset(): void {
  messagesStore.setData([]);
  isLoadingStore.setData(false);
}

// Error messages per HTTP status code
export const AGENT_ERROR_MESSAGES: Record<number, string> = {
  429: 'Service temporairement indisponible. Veuillez réessayer.',
  500: 'Une erreur est survenue. Veuillez réessayer.',
  504: 'Temps de réponse dépassé. Veuillez reformuler votre question.',
};

export function getAgentErrorMessage(status: number): string {
  return AGENT_ERROR_MESSAGES[status] ?? 'Une erreur est survenue. Veuillez réessayer.';
}

export async function sendMessage(content: string, context: AgentContext): Promise<void> {
  const userMessage: AgentMessage = {
    role: 'user',
    content,
    timestamp: Date.now(),
  };

  messagesStore.updateData(current => [...current, userMessage]);
  isLoadingStore.setData(true);

  try {
    const currentMessages = messagesStore.getData();
    const url = `${MAVISMART_BACKEND_URL}/agent-chat`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({
        messages: currentMessages.map(m => ({role: m.role, content: m.content})),
        modelTier: modelTierStore.getData(),
        context,
      }),
    });

    if (!res.ok) {
      handleHttpError(res.status);
      return;
    }

    const response = await res.json();
    const assistantMessage: AgentMessage = {
      role: 'assistant',
      content: response.response,
      sources: response.sources,
      metadata: response.metadata,
      timestamp: Date.now(),
    };

    messagesStore.updateData(current => [...current, assistantMessage]);
  } catch (error) {
    // Network error or unexpected failure
    addErrorMessage('Une erreur est survenue. Veuillez réessayer.');
    notifyError(error, {silent: true});
  } finally {
    isLoadingStore.setData(false);
  }
}

function handleHttpError(status: number): void {
  // 401: session expired — do nothing, login redirect happens naturally
  if (status === 401) return;

  // 403: close the panel (defensive, should not happen)
  if (status === 403) {
    togglePanel();
    return;
  }

  // 429, 500, 504: display a user-friendly error message in the conversation
  addErrorMessage(getAgentErrorMessage(status));
}

function addErrorMessage(content: string): void {
  const errorMessage: AgentMessage = {
    role: 'assistant',
    content,
    isError: true,
    timestamp: Date.now(),
  };
  messagesStore.updateData(current => [...current, errorMessage]);
}
